import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { trackPrimaryCta, trackSecondaryCta } from "@/lib/analytics";
import { RevealBlock } from "@/components/motion/primitives";
import { SignalMeta, SignalRule, SignalField } from "@/components/roc-signal";

interface CTAFinalProps {
  language: 'es' | 'en';
}

const content = {
  es: {
    index: "11 / SIGUIENTE PASO",
    meta: "REUNIÓN INICIAL GRATUITA DE 30 MINUTOS",
    h2: "Antes de invertir más en SEO, identifica dónde está la oportunidad.",
    copy: "Solicita una conversación estratégica para revisar tu situación actual, prioridades y objetivos de crecimiento.",
    primary: "Agendar reunión inicial gratuita de 30 minutos",
    primaryHref: "/diagnostico-seo-gratis",
    secondary: "Ver servicios SEO",
    secondaryHref: "/servicios",
  },
  en: {
    index: "11 / NEXT STEP",
    meta: "FREE 30-MINUTE INITIAL CONSULTATION",
    h2: "Before investing more in SEO, find out where the opportunity actually is.",
    copy: "Request a strategy conversation to review your current situation, priorities and growth objectives.",
    primary: "Book a free 30-minute initial consultation",
    primaryHref: "/en/free-seo-diagnosis",
    secondary: "View SEO services",
    secondaryHref: "/en/services",
  },
} as const;

export const CTAFinal = ({ language }: CTAFinalProps) => {
  const t = content[language];

  return (
    <section className="relative overflow-hidden border-t border-ink-line bg-ink text-paper">
      <SignalField tone="dark" />
      <span
        aria-hidden="true"
        className="pointer-events-none absolute -right-6 bottom-0 select-none font-display text-[20vw] font-semibold leading-none tracking-tighter text-paper/[0.035]"
      >
        ROC
      </span>

      <div className="roc-shell roc-section relative">
        <div className="flex flex-wrap items-center gap-x-8 gap-y-3">
          <SignalMeta tone="signal">{t.index}</SignalMeta>
          <SignalMeta tone="muted">{t.meta}</SignalMeta>
        </div>

        <SignalRule className="mt-8 max-w-4xl text-paper" />

        <RevealBlock className="mt-12 max-w-4xl">
          <h2 className="font-display text-[2.3rem] font-semibold leading-[0.98] tracking-[-0.04em] sm:text-[3.2rem] lg:text-[4.2rem]">
            {t.h2}
          </h2>
          <p className="mt-8 max-w-xl text-lg leading-relaxed text-stone-soft">{t.copy}</p>

          <div className="mt-12 flex flex-col items-start gap-x-10 gap-y-5 sm:flex-row sm:items-center">
            <Link
              to={t.primaryHref}
              onClick={() => trackPrimaryCta(t.primary, 'home_final_cta')}
              className="roc-cta roc-focus inline-flex min-h-12 items-center gap-3 bg-signal px-6 py-3.5 text-sm font-medium text-paper transition-colors duration-300 hover:bg-signal-soft"
            >
              {t.primary}
              <ArrowRight className="roc-cta-arrow h-4 w-4" aria-hidden="true" />
            </Link>

            <Link
              to={t.secondaryHref}
              onClick={() => trackSecondaryCta(t.secondary, 'home_final_cta')}
              className="roc-link roc-cta roc-focus inline-flex min-h-12 items-center gap-2 text-sm font-medium text-paper/80 hover:text-paper"
            >
              {t.secondary}
              <ArrowRight className="roc-cta-arrow h-4 w-4" aria-hidden="true" />
            </Link>
          </div>
        </RevealBlock>
      </div>
    </section>
  );
};
